import React from "react";
import { Box, Grid, Card, Typography, Chip, Button } from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import {
  Timeline,
  TrendingUp,
  TrendingDown,
  SentimentSatisfied,
  ArrowUpward,
  ArrowDownward,
  Insights,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

const TrendsTab = ({ country, currentCountryData, isLoggedIn }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isPositive = (value) => Number(value) > 0;
  const isNegative = (value) => Number(value) < 0;
  const isNeutral = (value) => Number(value) === 0;

  const bullishCount =
    currentCountryData.trends.marketSentiment.bullishMaterials;
  const bearishCount =
    currentCountryData.trends.marketSentiment.bearishMaterials;
  const topGainers = Array.isArray(currentCountryData.trends.topGainer)
    ? currentCountryData.trends.topGainer
    : currentCountryData.trends.topGainer
    ? [currentCountryData.trends.topGainer]
    : [];

  const topLosers = Array.isArray(currentCountryData.trends.topLoser)
    ? currentCountryData.trends.topLoser
    : currentCountryData.trends.topLoser
    ? [currentCountryData.trends.topLoser]
    : [];

  const handleLogin = () => {
    navigate("/login");
  };

  const handleRegister = () => {
    navigate("/register");
  };

  const renderNoDataMessage = (type) => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        py: 6,
        textAlign: "center",
        color: theme.palette.text.secondary,
        backgroundColor: alpha(theme.palette.grey[300], 0.05),
        borderRadius: 2,
        border: `1px dashed ${alpha(theme.palette.divider, 0.5)}`,
      }}
    >
      <SentimentSatisfied
        sx={{ fontSize: 48, mb: 2, color: theme.palette.text.disabled }}
      />
      <Typography variant="h6" fontWeight="medium">
        No {type} materials at the moment.
      </Typography>
      <Typography variant="body2" color="text.secondary" mt={1}>
        Data will appear once market activity occurs.
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ position: "relative" }}>
      <Box
        sx={{
          filter: !isLoggedIn ? "blur(4px)" : "none",
          pointerEvents: !isLoggedIn ? "none" : "auto",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            mb: 4,
            p: 3,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${alpha(
              theme.palette.secondary.main,
              0.08
            )} 0%, ${alpha(theme.palette.warning.main, 0.08)} 100%)`,
          }}
        >
          <Timeline
            sx={{ fontSize: 32, color: theme.palette.secondary.main }}
          />
          <Box>
            <Typography variant="h5" fontWeight="bold">
              Market Trends
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {country} Market • Performance trends and sentiment analysis
            </Typography>
          </Box>
        </Box>

        {/* <Card
          sx={{
            borderRadius: 4,
            boxShadow: theme.shadows[8],
            mb: 4,
            overflow: "hidden",
            border: `1px solid ${alpha(theme.palette.secondary.main, 0.1)}`,
          }}
        >
          <Box
            sx={{
              p: 3,
              background: `linear-gradient(135deg, ${alpha(
                theme.palette.secondary.main,
                0.1
              )} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
              borderBottom: `1px solid ${alpha(
                theme.palette.secondary.main,
                0.2
              )}`,
            }}
          >
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <Insights /> Market Sentiment Overview
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Real-time sentiment analysis across all materials
            </Typography>
          </Box>
          <Box sx={{ p: 4 }}>
            {bullishCount + bearishCount === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: "150px",
                  color: theme.palette.text.secondary,
                  fontStyle: "italic",
                }}
              >
                No sentiment data available.
              </Box>
            ) : (
              <Grid
                container
                spacing={4}
                alignItems="center"
                justifyContent="center"
              >
                <Grid item xs={12} md={4}>
                  <Box sx={{ textAlign: "center" }}>
                    <Box
                      sx={{
                        p: 3,
                        borderRadius: 3,
                        backgroundColor: alpha(theme.palette.success.main, 0.1),
                        mb: 2,
                      }}
                    >
                      <Typography
                        variant="h2"
                        fontWeight="bold"
                        color="success.main"
                      >
                        {bullishCount}
                      </Typography>
                      <Typography
                        variant="h6"
                        color="text.secondary"
                        fontWeight="medium"
                      >
                        <TrendingUp /> Bullish Materials
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ textAlign: "center" }}>
                    <Box
                      sx={{
                        p: 3,
                        borderRadius: 3,
                        background: `linear-gradient(135deg, ${alpha(
                          bullishCount > bearishCount
                            ? theme.palette.success.main
                            : theme.palette.error.main,
                          0.15
                        )} 0%, ${alpha(
                          bullishCount > bearishCount
                            ? theme.palette.success.main
                            : theme.palette.error.main,
                          0.05
                        )} 100%)`,
                        border: `2px solid ${alpha(
                          bullishCount > bearishCount
                            ? theme.palette.success.main
                            : theme.palette.error.main,
                          0.3
                        )}`,
                        mb: 2,
                      }}
                    >
                      <Typography
                        variant="h4"
                        fontWeight="bold"
                        sx={{ fontSize: "3rem", mb: 1 }}
                      >
                        {bullishCount > bearishCount ? (
                          <ArrowUpward color="success" fontSize="large" />
                        ) : (
                          <ArrowDownward color="error" fontSize="large" />
                        )}
                      </Typography>
                      <Typography variant="h6" fontWeight="bold">
                        {bullishCount > bearishCount
                          ? "Bullish Market"
                          : "Bearish Market"}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        height: 16,
                        borderRadius: 8,
                        backgroundColor: alpha(theme.palette.divider, 0.3),
                        overflow: "hidden",
                        position: "relative",
                      }}
                    >
                      <Box
                        sx={{
                          width: `${
                            (bullishCount / (bullishCount + bearishCount)) * 100
                          }%`,
                          height: "100%",
                          background: `linear-gradient(90deg, ${theme.palette.success.main} 0%, ${theme.palette.success.light} 100%)`,
                          borderRadius: 8,
                        }}
                      />
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ textAlign: "center" }}>
                    <Box
                      sx={{
                        p: 3,
                        borderRadius: 3,
                        backgroundColor: alpha(theme.palette.error.main, 0.1),
                        mb: 2,
                      }}
                    >
                      <Typography
                        variant="h2"
                        fontWeight="bold"
                        color="error.main"
                      >
                        {bearishCount}
                      </Typography>
                      <Typography
                        variant="h6"
                        color="text.secondary"
                        fontWeight="medium"
                      >
                        <TrendingDown /> Bearish Materials
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            )}
          </Box>
        </Card> */}

        {/* The responsive layout for the two cards below is handled 
        by a single Box component using CSS Grid, similar to your Price tab.
      */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr", // stacks cards on extra small screens
              md: "1fr 1fr", // displays cards side by side on medium and larger screens
            },
            gap: { xs: 2, md: 4 },
            mb: 4,
            alignItems: "stretch", // keeps cards the same height
          }}
        >
          {/* Top Gainers Card */}
          <Card
            sx={{
              borderRadius: 4,
              boxShadow: theme.shadows[8],
              border: `1px solid ${alpha(theme.palette.success.main, 0.1)}`,
              overflow: "hidden",
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                p: 3,
                background: `linear-gradient(135deg, ${alpha(
                  theme.palette.success.main,
                  0.1
                )} 0%, ${alpha(theme.palette.success.main, 0.05)} 100%)`,
                borderBottom: `1px solid ${alpha(
                  theme.palette.success.main,
                  0.2
                )}`,
              }}
            >
              <Typography
                variant="h6"
                fontWeight="bold"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  color: theme.palette.success.main,
                }}
              >
                <TrendingUp /> Top Gainers
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Materials with highest price increases
              </Typography>
            </Box>
            <Box sx={{ p: 3, flexGrow: 1 }}>
              {topGainers.length > 0
                ? topGainers.slice(0, 3).map((item, i) => (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        p: 2,
                        mb: i < 2 ? 2 : 0,
                        borderRadius: 2,
                        backgroundColor: alpha(
                          theme.palette.success.main,
                          0.05
                        ),
                        border: `1px solid ${alpha(
                          theme.palette.success.main,
                          0.1
                        )}`,
                        "&:hover": {
                          backgroundColor: alpha(
                            theme.palette.success.main,
                            0.1
                          ),
                          transform: "translateX(4px)",
                          transition: "all 0.2s ease",
                        },
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 2 }}
                      >
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: "50%",
                            backgroundColor: alpha(
                              theme.palette.success.main,
                              0.2
                            ),
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: "bold",
                            color: theme.palette.success.main,
                            fontSize: "14px",
                          }}
                        >
                          {i + 1}
                        </Box>
                        <Box>
                          <Typography variant="h6" fontWeight="bold">
                            {item.material}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {item.formatted_price}
                          </Typography>
                        </Box>
                      </Box>
                      <Chip
                        icon={<TrendingUp sx={{ fontSize: 16 }} />}
                        label={`${item.change > 0 ? "+" : ""}${item.change}`}
                        sx={{
                          backgroundColor: alpha(
                            theme.palette.success.main,
                            0.2
                          ),
                          color: theme.palette.success.main,
                          fontWeight: "bold",
                          border: `1px solid ${alpha(
                            theme.palette.success.main,
                            0.3
                          )}`,
                        }}
                      />
                    </Box>
                  ))
                : renderNoDataMessage("bullish")}
            </Box>
          </Card>

          {/* Top Losers Card */}
          <Card
            sx={{
              borderRadius: 4,
              boxShadow: theme.shadows[8],
              border: `1px solid ${alpha(theme.palette.error.main, 0.1)}`,
              overflow: "hidden",
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                p: 3,
                background: `linear-gradient(135deg, ${alpha(
                  theme.palette.error.main,
                  0.1
                )} 0%, ${alpha(theme.palette.error.main, 0.05)} 100%)`,
                borderBottom: `1px solid ${alpha(
                  theme.palette.error.main,
                  0.2
                )}`,
              }}
            >
              <Typography
                variant="h6"
                fontWeight="bold"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  color: theme.palette.error.main,
                }}
              >
                <TrendingDown /> Top Losers
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Materials with highest price decreases
              </Typography>
            </Box>
            <Box sx={{ p: 3, flexGrow: 1 }}>
              {topLosers.length > 0
                ? topLosers.slice(0, 3).map((item, i) => (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        p: 2,
                        mb: i < 2 ? 2 : 0,
                        borderRadius: 2,
                        backgroundColor: alpha(theme.palette.error.main, 0.05),
                        border: `1px solid ${alpha(
                          theme.palette.error.main,
                          0.1
                        )}`,
                        "&:hover": {
                          backgroundColor: alpha(theme.palette.error.main, 0.1),
                          transform: "translateX(4px)",
                          transition: "all 0.2s ease",
                        },
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 2 }}
                      >
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: "50%",
                            backgroundColor: alpha(
                              theme.palette.error.main,
                              0.2
                            ),
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: "bold",
                            color: theme.palette.error.main,
                            fontSize: "14px",
                          }}
                        >
                          {i + 1}
                        </Box>
                        <Box>
                          <Typography variant="h6" fontWeight="bold">
                            {item.material}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {item.formatted_price}
                          </Typography>
                        </Box>
                      </Box>
                      <Chip
                        icon={<TrendingDown sx={{ fontSize: 16 }} />}
                        label={item.change}
                        sx={{
                          backgroundColor: alpha(theme.palette.error.main, 0.2),
                          color: theme.palette.error.main,
                          fontWeight: "bold",
                          border: `1px solid ${alpha(
                            theme.palette.error.main,
                            0.3
                          )}`,
                        }}
                      />
                    </Box>
                  ))
                : renderNoDataMessage("bearish")}
            </Box>
          </Card>
        </Box>
      </Box>
      {/* CTA Overlay if not logged in */}
      {!isLoggedIn && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            gap: 2,
            backgroundColor: alpha(theme.palette.background.default, 0.6),
            borderRadius: 3,
          }}
        >
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
            Please log in to access volume insights
          </Typography>
          <Box sx={{ display: "flex", gap: 2 }}>
            {/* Log In Button */}
            <Button
              variant="contained"
              sx={{
                borderRadius: 2,
                px: 5,
                py: 1.5,
                backgroundColor: "#F4C542",
                color: "black",
                fontWeight: "bold",
                "&:hover": {
                  backgroundColor: "#E3AE27",
                },
              }}
              onClick={handleLogin}
            >
              Login
            </Button>

            {/* Register Button */}
            <Button
              variant="contained"
              sx={{
                borderRadius: 2,
                px: 5,
                py: 1.5,
                backgroundColor: theme.palette.common.black,
                color: theme.palette.common.white,
                fontWeight: "bold",
                "&:hover": {
                  backgroundColor: theme.palette.grey[900],
                },
              }}
              onClick={handleRegister}
            >
              Register
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default TrendsTab;
